import type { Money } from 'src/feature/CardDetailHero/cardDetailHeroTypes'

/**
 * Frozen demo data for the card-detail Statements tab (EX-40600).
 *
 * Mirrors the shape and conventions `cardDetailHeroTypes` already set for the
 * sibling Card Management surface, deliberately: money is `Money` (minor units
 * plus an ISO-4217 currency, never a float), dates are ISO-8601, and enum
 * spellings are SCREAMING_SNAKE like the capability contract. Wiring the real
 * endpoint should therefore be a mapping change in `useAccountStatementsData`
 * rather than a rename sweep through every component below.
 *
 * EVERY FIGURE HERE IS FABRICATED — intentionally, as a demo surface for
 * selected organizations until the real endpoint lands. See
 * `useAccountStatementsData` for how the audience is actually determined.
 *
 * The grouping is by source rather than by consumer: payments and statements are
 * separately fetched in any plausible contract, so each region reads only the
 * group it needs and a later failure in one cannot blank the other.
 */

/**
 * The contract's public status vocabulary, verbatim
 * (`CardManagementStatementStatus`):
 *
 * - `DUE` — an unpaid amount remains and the due date has not passed.
 * - `PARTIALLY_PAID` — a payment applied, an amount remains, not yet past due.
 * - `PAID` — nothing remains, whether settled by payment or by credit.
 * - `PAST_DUE` — an amount remains after the due date.
 *
 * The server decides when a statement crosses into `PAST_DUE`; never re-derive
 * it by comparing `dueOn` to the browser clock, which does not know the card's
 * billing timezone.
 */
export type StatementStatus = 'DUE' | 'PARTIALLY_PAID' | 'PAID' | 'PAST_DUE'

/**
 * The contract's document-format vocabulary
 * (`CardManagementStatementDocumentFormat`), verbatim.
 */
export type StatementDocumentFormat = 'PDF' | 'XLSX' | 'CSV'

/**
 * Stable presentation order for configured statement-document formats.
 */
export const STATEMENT_DOCUMENT_FORMATS: StatementDocumentFormat[] = [
  'PDF',
  'XLSX',
  'CSV',
]

/** Every non-`PAID` status still owes something, and they share a row anatomy. */
export const isStatementPaid = (status: StatementStatus): boolean =>
  status === 'PAID'

export interface StatementTransaction {
  id: string
  /** ISO-8601 date the transaction posted to the statement. */
  postedOn: string
  description: string
  amount: Money
}

export interface StatementSummary {
  id: string
  /**
   * Any date within the statement period, ISO-8601. Rendered as the period
   * label ("June 2026"); the design never shows a start/end range.
   */
  period: string
  /** ISO-8601 date the statement payment is due. */
  dueOn: string
  status: StatementStatus
  statementAmount: Money
  amountOwed: Money
  /** Present only once some amount has actually been applied. */
  amountPaid?: Money
  /** Configured public document locations; missing keys are unavailable. */
  documentUrls: Partial<Record<StatementDocumentFormat, string>>
  /**
   * The statement's ledger.
   *
   * Present only on DEMO statements, which embed it. Real statements load their
   * line items lazily from `GET .../statements/{id}/items` when the detail
   * modal opens — the list is separately paginated on the wire and would
   * otherwise cost one request per row on tab load.
   */
  transactions?: StatementTransaction[]
}

/** The contract's public payment-method vocabulary (`CardManagementPaymentType`). */
export type StatementPaymentMethod =
  | 'BANK_TRANSFER'
  | 'CHECK'
  | 'WIRE'
  | 'OTHER'

/**
 * The contract's public payment-record vocabulary
 * (`CardManagementPaymentStatus`). `PARTIALLY_PAID` is deliberately absent —
 * partial payment is a statement-level condition, not a payment status.
 */
export type StatementPaymentStatus = 'PENDING' | 'PAID' | 'FAILED' | 'REVERSED'

export interface StatementPayment {
  id: string
  amount: Money
  /** ISO-8601 instant the payment attempt or posting took effect. */
  paidOn: string
  method: StatementPaymentMethod
  status: StatementPaymentStatus
}

/**
 * The next obligation, or `null` when there is none.
 *
 * Nullable deliberately: "nothing is due" is a real answer from the payments
 * summary, not a missing one, and a model that cannot express it forces the
 * caller to either invent a zero or discard a live read. Both happened — see
 * `useAccountStatementsData`.
 */
export interface NextPaymentDue {
  amount: Money
  /** ISO-8601 date the payment is due. */
  dueOn: string
  /**
   * From the server's `status`, never re-derived by comparing `dueOn` to the
   * browser clock — the boundary is a billing-calendar date in the card's own
   * timezone, which the client does not own.
   */
  isPastDue: boolean
}

export interface AccountStatementsData {
  payments: {
    /** `null` when nothing is due; the section renders its settled state. */
    nextPaymentDue: NextPaymentDue | null
    history: StatementPayment[]
  }
  statements: {
    /** Shown on load, newest first — the current statement is the first row. */
    recent: StatementSummary[]
    /** Revealed in place by "See more statements". */
    older: StatementSummary[]
  }
}

const usd = (amountMinor: number): Money => ({ amountMinor, currency: 'USD' })

/** Merchant/amount pool the demo ledgers cycle through. Minor units. */
const LEDGER_POOL: ReadonlyArray<{ description: string; amountMinor: number }> =
  [
    { description: 'Delta Air Lines', amountMinor: 84230 },
    { description: 'Cloud Hosting Co.', amountMinor: 43018 },
    { description: 'The Bowery Hotel', amountMinor: 121000 },
    { description: 'Uber', amountMinor: 2840 },
    { description: 'LinkedIn Ads', amountMinor: 110000 },
    { description: 'Sweetgreen', amountMinor: 1875 },
    { description: 'Figma', amountMinor: 14400 },
    { description: 'The Smith', amountMinor: 21850 },
    { description: 'Spotify Ads', amountMinor: 41000 },
    { description: 'Notion', amountMinor: 9600 },
    { description: 'WeWork', amountMinor: 24000 },
    { description: 'Lyft', amountMinor: 2210 },
    { description: 'Adobe', amountMinor: 59988 },
    { description: 'United Airlines', amountMinor: 134000 },
    { description: 'DoorDash', amountMinor: 6430 },
  ]

/**
 * Builds a ledger long enough that the statement modal's transaction list
 * actually needs its scroll container — the design's one scroll-ownership
 * guardrail is that the summary, column header, and total stay pinned while only
 * the rows scroll, which a three-row ledger would never exercise.
 *
 * Deterministic (no randomness, no clock) so tests and screenshots are stable.
 */
const buildLedger = (
  statementId: string,
  year: number,
  monthIndex: number,
  count: number,
): StatementTransaction[] =>
  Array.from({ length: count }, (_, index) => {
    const entry = LEDGER_POOL[index % LEDGER_POOL.length]
    const dayOfMonth = Math.max(1, 28 - index)

    return {
      id: `${statementId}-txn-${index}`,
      postedOn: `${year}-${String(monthIndex).padStart(2, '0')}-${String(
        dayOfMonth,
      ).padStart(2, '0')}`,
      description: entry.description,
      amount: usd(entry.amountMinor),
    }
  })

/**
 * Builds one statement whose figures RECONCILE with its own ledger.
 *
 * The design prototype hardcodes a statement amount independently of its
 * synthetic transaction list, so its modal shows a total that disagrees with the
 * statement it belongs to. Sample figures are non-binding but internal
 * arithmetic is not — a statement whose charges do not add up to its balance
 * reads as a bug on screen. The row count is what varies per period here, and
 * every figure derives from it.
 */
const buildStatement = ({
  id,
  year,
  monthIndex,
  dueOn,
  status,
  ledgerRows,
}: {
  id: string
  year: number
  monthIndex: number
  dueOn: string
  status: StatementStatus
  ledgerRows: number
}): StatementSummary => {
  const transactions = buildLedger(id, year, monthIndex, ledgerRows)
  const totalMinor = transactions.reduce(
    (runningTotal, transaction) =>
      runningTotal + transaction.amount.amountMinor,
    0,
  )

  return {
    id,
    period: `${year}-${String(monthIndex).padStart(2, '0')}-01`,
    dueOn,
    status,
    statementAmount: usd(totalMinor),
    amountOwed: usd(totalMinor),
    ...(status === 'PAID' ? { amountPaid: usd(totalMinor) } : {}),
    documentUrls: {},
    transactions,
  }
}

const recent: StatementSummary[] = [
  {
    id: 'statement-2026-06',
    year: 2026,
    monthIndex: 6,
    dueOn: '2026-07-15',
    status: 'DUE' as const,
    ledgerRows: 24,
  },
  {
    id: 'statement-2026-05',
    year: 2026,
    monthIndex: 5,
    dueOn: '2026-06-15',
    status: 'PAID' as const,
    ledgerRows: 21,
  },
  {
    id: 'statement-2026-04',
    year: 2026,
    monthIndex: 4,
    dueOn: '2026-05-15',
    status: 'PAID' as const,
    ledgerRows: 17,
  },
  {
    id: 'statement-2026-03',
    year: 2026,
    monthIndex: 3,
    dueOn: '2026-04-15',
    status: 'PAID' as const,
    ledgerRows: 26,
  },
].map(buildStatement)

const older: StatementSummary[] = [
  {
    id: 'statement-2026-02',
    year: 2026,
    monthIndex: 2,
    dueOn: '2026-03-15',
    status: 'PAID' as const,
    ledgerRows: 19,
  },
  {
    id: 'statement-2026-01',
    year: 2026,
    monthIndex: 1,
    dueOn: '2026-02-15',
    status: 'PAID' as const,
    ledgerRows: 23,
  },
  {
    id: 'statement-2025-12',
    year: 2025,
    monthIndex: 12,
    dueOn: '2026-01-15',
    status: 'PAID' as const,
    ledgerRows: 28,
  },
  {
    id: 'statement-2025-11',
    year: 2025,
    monthIndex: 11,
    dueOn: '2025-12-15',
    status: 'PAID' as const,
    ledgerRows: 16,
  },
].map(buildStatement)

const [openStatement] = recent

export const ACCOUNT_STATEMENTS_DEMO: AccountStatementsData = {
  payments: {
    // The next payment due is the open statement's balance on its own due date.
    // The design shows a smaller standalone figure, which reads as a minimum
    // payment but is never labelled as one — deriving it keeps the tab's two
    // views of the same obligation from contradicting each other.
    nextPaymentDue: {
      amount: openStatement.amountOwed,
      dueOn: openStatement.dueOn,
      isPastDue: false,
    },
    // Payment history is what was actually paid against the settled statements,
    // so the figures match those rows rather than floating free of them.
    history: recent
      .filter((statement) => statement.status === 'PAID')
      .map((statement) => ({
        id: `payment-${statement.id}`,
        amount: statement.amountPaid ?? statement.amountOwed,
        paidOn: statement.dueOn,
        method: 'BANK_TRANSFER' as const,
        status: 'PAID' as const,
      })),
  },
  statements: { recent, older },
}
